import * as Api from '@ViewModels';
import { useCallback, useState } from 'react';

/**
 * Finds the company tag value from an array of filter criteria.
 * @param criteria - Array of contact filter criteria to search
 * @returns The company tag value if found, empty string otherwise
 */
const findCompanyTagValue = (criteria: Api.IContactFilterCriteria[]): string => {
	const criterion = criteria.find(c => c.property === Api.ContactFilterCriteriaProperty.CompanyTag);
	return criterion?.value ?? '';
};

/**
 * Hook for managing company tag filter state in the AdvancedFilters context.
 * Provides methods to get, set, clear, and check company tag filter criteria.
 *
 * @param initialFilterCriteria - Initial filter criteria to extract company tag from
 * @returns Object containing companyTag value and methods to manipulate it
 */
export const useCompanyTagFilters = (initialFilterCriteria: Api.IContactFilterCriteria[] = []) => {
	const [companyTag, setCompanyTag] = useState(() => findCompanyTagValue(initialFilterCriteria));

	const getCompanyTagCriteria = useCallback((): Api.IContactFilterCriteria[] => {
		const criteria: Api.IContactFilterCriteria[] = [];

		if (companyTag.trim()) {
			criteria.push({
				property: Api.ContactFilterCriteriaProperty.CompanyTag,
				value: companyTag.trim(),
			});
		}

		return criteria;
	}, [companyTag]);

	const refreshCompanyTagFilters = useCallback((criteria: Api.IContactFilterCriteria[]) => {
		setCompanyTag(findCompanyTagValue(criteria));
	}, []);

	const clearCompanyTagFilters = useCallback(() => {
		setCompanyTag('');
	}, []);

	const hasCompanyTagFilters = companyTag.trim() !== '';

	const isCompanyTagCriteria = useCallback((criterion: Api.IContactFilterCriteria): boolean => {
		return criterion.property === Api.ContactFilterCriteriaProperty.CompanyTag;
	}, []);

	return {
		companyTag,
		setCompanyTag,
		getCompanyTagCriteria,
		refreshCompanyTagFilters,
		clearCompanyTagFilters,
		hasCompanyTagFilters,
		isCompanyTagCriteria,
	};
};
